// Canvas2D dungeon rendering. Camera is role-aware: the architect sees more
// rows at once than a hero does (design doc: "let's do x2 for now") and
// focuses on the active piece, while each hero's own client focuses on
// their own hero.

import { CONFIG } from "../shared/config";
import { pieceHeight, pieceOffsets, pieceWidth, type PieceType } from "../shared/pieces";
import { HERO_SPELL_DESCRIPTIONS } from "../shared/spellInfo";
import type {
  ArchitectAbility,
  ArchitectUpgrade,
  DungeonSnapshot,
  GroundEffectSnapshot,
  HeroClass,
  HeroSnapshot,
  MinionSnapshot,
  MinionUpgradeLevels,
  Role,
  SpecialCellKind,
  SpellSlot,
  StatusKind,
  StatusSnapshot,
  WorldPoint,
} from "../shared/types";

/** Any spell config object from shared/config.ts's CONFIG.heroSpells — fields present decide what the arm preview looks like (see drawHeroSpellPreview). */
type SpellConfig = {
  cooldown: number;
  range?: number;
  radius?: number;
  width?: number;
  distance?: number;
  allyRadius?: number;
};

export interface Camera {
  tilePx: number;
  xOffset: number; // px, left edge of the board on screen
  topRow: number; // dungeon row shown at the top edge of the canvas
}

const HERO_COLORS: Record<string, string> = {
  hero1: "#e05a5a",
  hero2: "#5a9ee0",
  hero3: "#e0c040",
};

const CLASS_LABELS: Record<HeroClass, string> = {
  warrior: "W",
  hunter: "H",
  mage: "M",
};

export function computeCamera(canvas: HTMLCanvasElement, dungeon: DungeonSnapshot, heroes: readonly HeroSnapshot[], role: Role | null): Camera {
  const viewportRows = role === "architect" ? CONFIG.architectViewportRows : CONFIG.heroViewportRows;
  const tilePx = Math.max(4, Math.min(canvas.width / dungeon.width, canvas.height / viewportRows));
  const xOffset = (canvas.width - dungeon.width * tilePx) / 2;

  const myHero = role ? heroes.find((h) => h.role === role) : undefined;
  let focusRow: number;
  if (myHero) {
    if (myHero.hp > 0) {
      focusRow = myHero.y;
    } else {
      // Dead heroes have nothing of their own worth looking at — follow
      // whoever's still fighting instead of staring at a frozen corpse.
      const aliveHeroes = heroes.filter((h) => h.hp > 0);
      focusRow = aliveHeroes.length > 0 ? aliveHeroes.reduce((sum, h) => sum + h.y, 0) / aliveHeroes.length : myHero.y;
    }
  } else {
    const pieceRows = dungeon.activePiece.cells.map((c) => c[1]);
    const pieceFocusRow = pieceRows.reduce((sum, r) => sum + r, 0) / pieceRows.length;
    // The architect's camera needs to show both the active piece (near the
    // front) and the already-built dungeon behind it, where heroes fight
    // minions — focusing on the piece alone left minions permanently
    // off-screen. Splitting the difference keeps the piece comfortably in
    // view (it's usually only a few rows ahead of the frontier) while
    // pulling the built area into frame too.
    focusRow = role === "architect" ? (dungeon.frontierRow + pieceFocusRow) / 2 : pieceFocusRow;
  }
  const topRow = focusRow + viewportRows / 2;
  return { tilePx, xOffset, topRow };
}

// A continuous world-space point (a hero's position, or the flame barrier's
// row, in tile units — see server/heroes.ts) maps directly to screen space,
// no corner offset.
function pointToScreen(camera: Camera, x: number, y: number): { x: number; y: number } {
  return { x: camera.xOffset + x * camera.tilePx, y: (camera.topRow - y) * camera.tilePx };
}

/** Inverse of pointToScreen — used to turn the mouse cursor into a world-space aim point (see src/input.ts). */
export function screenToWorld(camera: Camera, screenX: number, screenY: number): WorldPoint {
  return { x: (screenX - camera.xOffset) / camera.tilePx, y: camera.topRow - screenY / camera.tilePx };
}

// A tile *square* identified by integer (col,row) spans world space
// [col,col+1) x [row,row+1). The X axis lines up with screen X, so `col` is
// already the square's left edge — but the Y axis is inverted on screen
// (increasing row = further forward = higher up, smaller screen Y), so the
// square's top edge is the row+1 boundary, not the row boundary itself.
// Using pointToScreen's formula directly here (as an earlier version did)
// draws every tile one row further "back" than where hero/collision logic
// actually considers it walkable — heroes could visibly wander into the
// void because the void's tile-square was drawn one row short of the real
// boundary.
function tileToScreen(camera: Camera, col: number, row: number): { x: number; y: number } {
  return { x: camera.xOffset + col * camera.tilePx, y: (camera.topRow - row - 1) * camera.tilePx };
}

function fillTiles(ctx: CanvasRenderingContext2D, camera: Camera, tiles: readonly [number, number][], color: string): void {
  ctx.fillStyle = color;
  for (const [col, row] of tiles) {
    const { x, y } = tileToScreen(camera, col, row);
    ctx.fillRect(x, y, camera.tilePx, camera.tilePx);
  }
}

const GROUND_EFFECT_COLORS: Record<GroundEffectSnapshot["kind"], string> = {
  mageDamageGlyph: "#c04a3a",
  mageHealGlyph: "#5ad17a",
  hunterPiercingShot: "#e0e060",
  warriorAoePulse: "#e0704a",
  meteorTelegraph: "#ff6a2a",
  spawnTelegraph: "#a03ad1",
};

/** Colors for the generic per-character status-effect chips (see drawStatusChips) — every StatusKind gets one, so "any status that affects a character" is always visible to everyone at the table, not just in the owning player's own HUD text. */
const STATUS_COLORS: Record<StatusKind, string> = {
  dot: "#c04a3a",
  hot: "#5ad17a",
  slowed: "#5a8ec0",
  damageReduction: "#9a9ab0",
  attackRateBuff: "#e0c040",
  moveSpeedBuff: "#5ad1a8",
  rangeBuff: "#a03ad1",
};

/** A small row of colored chips, one per active status kind, centered above (x, aboveY) — used for both heroes and minions so any status shows up the same way regardless of who/what it's on. */
function drawStatusChips(ctx: CanvasRenderingContext2D, x: number, aboveY: number, statuses: readonly StatusSnapshot[]): void {
  if (statuses.length === 0) return;
  const chipSize = 6;
  const gap = 3;
  const totalWidth = statuses.length * chipSize + (statuses.length - 1) * gap;
  let cx = x - totalWidth / 2;
  for (const status of statuses) {
    ctx.fillStyle = STATUS_COLORS[status.kind];
    ctx.beginPath();
    ctx.arc(cx + chipSize / 2, aboveY, chipSize / 2, 0, Math.PI * 2);
    ctx.fill();
    cx += chipSize + gap;
  }
}

/** Small padlock glyph centered at (cx, cy) — doors. */
function drawDoorIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number): void {
  ctx.strokeStyle = "#1c140a";
  ctx.lineWidth = Math.max(1.5, s * 0.15);
  ctx.beginPath();
  ctx.arc(cx, cy - s * 0.15, s * 0.35, Math.PI, 0);
  ctx.stroke();
  ctx.fillStyle = "#1c140a";
  ctx.fillRect(cx - s * 0.4, cy - s * 0.15, s * 0.8, s * 0.55);
}

/** Small key glyph — pickups. */
function drawKeyIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number): void {
  ctx.strokeStyle = "#3a2a08";
  ctx.lineWidth = Math.max(1.5, s * 0.18);
  ctx.beginPath();
  ctx.arc(cx - s * 0.25, cy, s * 0.3, 0, Math.PI * 2);
  ctx.moveTo(cx, cy);
  ctx.lineTo(cx + s * 0.55, cy);
  ctx.moveTo(cx + s * 0.35, cy);
  ctx.lineTo(cx + s * 0.35, cy + s * 0.25);
  ctx.stroke();
}

/** Small chest glyph — gold pickups the architect doesn't want reached. */
function drawChestIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number): void {
  ctx.fillStyle = "#8a5a2a";
  ctx.fillRect(cx - s * 0.45, cy - s * 0.3, s * 0.9, s * 0.6);
  ctx.strokeStyle = "#3a2408";
  ctx.lineWidth = Math.max(1.5, s * 0.12);
  ctx.strokeRect(cx - s * 0.45, cy - s * 0.3, s * 0.9, s * 0.6);
  ctx.beginPath();
  ctx.moveTo(cx - s * 0.45, cy);
  ctx.lineTo(cx + s * 0.45, cy);
  ctx.stroke();
}

const HAZARD_ICON_KIND: Record<SpecialCellKind, (ctx: CanvasRenderingContext2D, cx: number, cy: number, s: number) => void> = {
  door: drawDoorIcon,
  key: drawKeyIcon,
  chest: drawChestIcon,
};

/** Doors (block heroes only — filled like an obstacle), keys/chests (pickups sitting on open floor), and hazard glyphs (design doc extension). Always visible to everyone — "we always want to draw AOEs" extends to hazards too. */
function drawDoorsKeysChestsAndHazards(ctx: CanvasRenderingContext2D, camera: Camera, dungeon: DungeonSnapshot): void {
  // Doors read as a solid obstacle, same visual language as foundations.
  fillTiles(ctx, camera, dungeon.doors, "#5a3a1a");
  for (const [col, row] of dungeon.doors) {
    const { x, y } = tileToScreen(camera, col, row);
    drawDoorIcon(ctx, x + camera.tilePx / 2, y + camera.tilePx / 2, camera.tilePx * 0.6);
  }

  for (const [col, row] of dungeon.keys) {
    const { x, y } = tileToScreen(camera, col, row);
    drawKeyIcon(ctx, x + camera.tilePx / 2, y + camera.tilePx / 2, camera.tilePx * 0.6);
  }

  for (const chest of dungeon.chests) {
    const { x, y } = tileToScreen(camera, chest.cell[0], chest.cell[1]);
    drawChestIcon(ctx, x + camera.tilePx / 2, y + camera.tilePx / 2, camera.tilePx * 0.6);
  }

  for (const glyph of dungeon.hazardGlyphs) {
    const { x, y } = tileToScreen(camera, glyph.cell[0], glyph.cell[1]);
    if (glyph.kind === "dot") {
      // Permanent hazard zone — a persistent tinted tile, same treatment as gold-line rows.
      ctx.save();
      ctx.globalAlpha = 0.3;
      ctx.fillStyle = "#7a3ac0";
      ctx.fillRect(x, y, camera.tilePx, camera.tilePx);
      ctx.restore();
    } else {
      // One-time trap — a small warning triangle, not a full tile tint (it's consumed after one trigger).
      const cx = x + camera.tilePx / 2;
      const cy = y + camera.tilePx / 2;
      const s = camera.tilePx * 0.28;
      ctx.fillStyle = "#e0a040";
      ctx.beginPath();
      ctx.moveTo(cx, cy - s);
      ctx.lineTo(cx + s, cy + s * 0.8);
      ctx.lineTo(cx - s, cy + s * 0.8);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = "#1c1c26";
      ctx.font = `bold ${Math.round(s)}px system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("!", cx, cy + s * 0.15);
      ctx.textAlign = "left";
    }
  }
}

function drawGroundEffects(ctx: CanvasRenderingContext2D, camera: Camera, effects: readonly GroundEffectSnapshot[]): void {
  for (const effect of effects) {
    const color = GROUND_EFFECT_COLORS[effect.kind];
    const pct = effect.totalDuration > 0 ? effect.remaining / effect.totalDuration : 0;

    if (effect.kind === "hunterPiercingShot") {
      const a = pointToScreen(camera, effect.x, effect.y);
      const b = pointToScreen(camera, effect.x2 ?? effect.x, effect.y2 ?? effect.y);
      ctx.save();
      ctx.globalAlpha = Math.max(0, pct);
      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(2, effect.radius * 2 * camera.tilePx);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
      ctx.restore();
      continue;
    }

    const center = pointToScreen(camera, effect.x, effect.y);
    const r = effect.radius * camera.tilePx;
    const isTelegraph = effect.kind === "meteorTelegraph" || effect.kind === "spawnTelegraph";

    ctx.save();
    if (isTelegraph) {
      // Telegraphs fill in as the countdown runs out — "the effect is shown
      // a few seconds in advance" (design doc) — so it reads as "incoming".
      ctx.globalAlpha = 0.15 + 0.35 * (1 - pct);
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(center.x, center.y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 0.9;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.arc(center.x, center.y, r, 0, Math.PI * 2);
      ctx.stroke();
    } else {
      // Glyphs pulse gently and fade out over their last moment.
      ctx.globalAlpha = 0.28 * Math.min(1, pct * 4);
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(center.x, center.y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 0.7;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(center.x, center.y, r, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }
}

/** What the armed-ability targeting reticle should look like — see src/input.ts's ability-arm state. */
export interface AbilityPreview {
  ability: ArchitectAbility;
  point: WorldPoint;
}

const ABILITY_PREVIEW_RADIUS: Record<ArchitectAbility, number> = {
  meteor: CONFIG.meteorRadius,
  spawnMinions: 0.6,
  spawnSuperMinion: 0.9,
};

/** What an armed hero spell's preview should look like — see src/input.ts's spell-arm state. */
export interface HeroSpellPreview {
  heroClass: HeroClass;
  slot: SpellSlot;
  caster: WorldPoint;
  aim: WorldPoint;
}

/** The 2-line "how do I confirm this" instruction that follows the cursor while a spell is armed (see shared/spellInfo.ts's spellCastHintLines) — players kept missing that casting needs a follow-up click. */
export interface SpellCastHint {
  screen: { x: number; y: number };
  lines: readonly [string, string];
}

function clampToRangeClient(from: WorldPoint, target: WorldPoint, range: number): WorldPoint {
  const dx = target.x - from.x;
  const dy = target.y - from.y;
  const dist = Math.hypot(dx, dy);
  if (dist <= range || dist < 1e-6) return target;
  return { x: from.x + (dx / dist) * range, y: from.y + (dy / dist) * range };
}

/**
 * Shown while a spell is armed, waiting for a click to confirm — "the range
 * is shown, and the preview of the AoE (if relevant) is shown". Reads
 * whichever of range/radius/width/distance/allyRadius the spell's config
 * (shared/config.ts's CONFIG.heroSpells) actually has, so each spell gets a
 * sensible preview without a bespoke case per spell:
 * - `range` alone: a circle showing how far the cast can reach.
 * - `range` + `radius`: an AOE circle at the (range-clamped) aim point.
 * - `range` + `width`: a line preview (skill shot), same look as the
 *   in-flight hunterPiercingShot ground effect.
 * - `distance` (no `range`): a line toward the aim direction showing the
 *   leap's reach.
 * - `radius` alone (no `range`): a static circle around the caster (a
 *   self-centered AOE with no aim, e.g. Warrior's spell 1).
 * - `allyRadius`: an extra static circle around the caster showing who gets
 *   swept along (Mage's teleport).
 * A pure self-buff with none of these still gets the caster ring drawn by
 * the hero-statuses code once the buff actually lands — nothing to preview
 * here beforehand, so this function simply draws nothing extra for it.
 */
function drawHeroSpellPreview(ctx: CanvasRenderingContext2D, camera: Camera, preview: HeroSpellPreview): void {
  const cfg = (CONFIG.heroSpells[preview.heroClass] as Record<string, SpellConfig>)[`spell${preview.slot}`];
  const casterScreen = pointToScreen(camera, preview.caster.x, preview.caster.y);

  ctx.save();
  ctx.strokeStyle = "rgba(255, 255, 255, 0.55)";
  ctx.lineWidth = 1.5;
  ctx.setLineDash([4, 4]);

  if (cfg.range !== undefined) {
    ctx.beginPath();
    ctx.arc(casterScreen.x, casterScreen.y, cfg.range * camera.tilePx, 0, Math.PI * 2);
    ctx.stroke();
  }

  if (cfg.allyRadius !== undefined) {
    ctx.beginPath();
    ctx.arc(casterScreen.x, casterScreen.y, cfg.allyRadius * camera.tilePx, 0, Math.PI * 2);
    ctx.stroke();
  }

  ctx.setLineDash([]);
  ctx.fillStyle = "rgba(224, 224, 96, 0.3)";
  ctx.strokeStyle = "#e0e060";

  if (cfg.radius !== undefined && cfg.range !== undefined) {
    const point = clampToRangeClient(preview.caster, preview.aim, cfg.range);
    const p = pointToScreen(camera, point.x, point.y);
    ctx.beginPath();
    ctx.arc(p.x, p.y, cfg.radius * camera.tilePx, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  } else if (cfg.radius !== undefined) {
    // Self-centered AOE, no aim involved (e.g. Warrior spell 1).
    ctx.beginPath();
    ctx.arc(casterScreen.x, casterScreen.y, cfg.radius * camera.tilePx, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  } else if (cfg.width !== undefined && cfg.range !== undefined) {
    const dx = preview.aim.x - preview.caster.x;
    const dy = preview.aim.y - preview.caster.y;
    const dist = Math.hypot(dx, dy) || 1;
    const end = { x: preview.caster.x + (dx / dist) * cfg.range, y: preview.caster.y + (dy / dist) * cfg.range };
    const endScreen = pointToScreen(camera, end.x, end.y);
    ctx.lineWidth = Math.max(2, cfg.width * camera.tilePx);
    ctx.beginPath();
    ctx.moveTo(casterScreen.x, casterScreen.y);
    ctx.lineTo(endScreen.x, endScreen.y);
    ctx.stroke();
  } else if (cfg.distance !== undefined) {
    const dx = preview.aim.x - preview.caster.x;
    const dy = preview.aim.y - preview.caster.y;
    const dist = Math.hypot(dx, dy) || 1;
    const travel = Math.min(cfg.distance, dist);
    const end = { x: preview.caster.x + (dx / dist) * travel, y: preview.caster.y + (dy / dist) * travel };
    const endScreen = pointToScreen(camera, end.x, end.y);
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(casterScreen.x, casterScreen.y);
    ctx.lineTo(endScreen.x, endScreen.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(endScreen.x, endScreen.y, 5, 0, Math.PI * 2);
    ctx.fill();
  } else if (cfg.range !== undefined) {
    // Range-only (e.g. Hunter spell 3's target-ally): mark the clamped aim point.
    const point = clampToRangeClient(preview.caster, preview.aim, cfg.range);
    const p = pointToScreen(camera, point.x, point.y);
    ctx.beginPath();
    ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

/** Draws the 2-line cast instruction just below-right of the cursor — see SpellCastHint. */
function drawSpellCastHint(ctx: CanvasRenderingContext2D, hint: SpellCastHint): void {
  ctx.save();
  ctx.font = "13px system-ui, sans-serif";
  ctx.textBaseline = "top";
  const x = hint.screen.x + 14;
  const y = hint.screen.y + 14;
  const padding = 6;
  const lineHeight = 16;
  const width = Math.max(...hint.lines.map((l) => ctx.measureText(l).width)) + padding * 2;
  ctx.fillStyle = "rgba(15, 15, 22, 0.82)";
  ctx.fillRect(x - padding, y - padding, width, lineHeight * hint.lines.length + padding * 2);
  ctx.fillStyle = "#e8e8f0";
  hint.lines.forEach((line, i) => ctx.fillText(line, x, y + i * lineHeight));
  ctx.restore();
}

/**
 * The one or the other of the two bars a corpse gets, right of its X mark:
 * a filling green bar while a living hero is actively channeling a revive
 * (see CONFIG.heroCorpseReviveSeconds), or — before anyone's started one —
 * a draining orange bar counting down the window left to start
 * (CONFIG.heroCorpseReviveDeadlineSeconds). Nothing once that window has
 * closed with no channel underway; the dead player's own center-screen
 * auto-respawn countdown (drawDeathCountdownCenter) is the only cue left at
 * that point.
 */
function drawCorpseReviveBar(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, hero: HeroSnapshot): void {
  let pct: number;
  let color: string;
  if (hero.reviveChannelSeconds > 0) {
    pct = hero.reviveChannelSeconds / CONFIG.heroCorpseReviveSeconds;
    color = "#5ad17a";
  } else if (hero.timeSinceDeath < CONFIG.heroCorpseReviveDeadlineSeconds) {
    pct = 1 - hero.timeSinceDeath / CONFIG.heroCorpseReviveDeadlineSeconds;
    color = "#e0a040";
  } else {
    return;
  }
  pct = Math.max(0, Math.min(1, pct));

  const barW = 5;
  const barH = r * 1.6;
  const barX = x + r + 4;
  const barY = y - barH / 2;
  ctx.fillStyle = "#1c1c26";
  ctx.fillRect(barX, barY, barW, barH);
  const filledH = barH * pct;
  ctx.fillStyle = color;
  ctx.fillRect(barX, barY + (barH - filledH), barW, filledH); // fills from the bottom up
  ctx.strokeStyle = "rgba(255, 255, 255, 0.2)";
  ctx.lineWidth = 1;
  ctx.strokeRect(barX + 0.5, barY + 0.5, barW, barH);
}

/** Shown only on the dead hero's own screen — "Dead heroes are shown a countdown at the center of the screen" for the auto-respawn timer (see CONFIG.heroAutoRespawnSeconds). */
function drawDeathCountdownCenter(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, hero: HeroSnapshot): void {
  const remaining = Math.max(0, CONFIG.heroAutoRespawnSeconds - hero.timeSinceDeath);
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "rgba(224, 90, 90, 0.9)";
  ctx.font = "bold 28px system-ui, sans-serif";
  ctx.fillText("You have fallen", canvas.width / 2, canvas.height / 2 - 18);
  ctx.font = "20px system-ui, sans-serif";
  ctx.fillStyle = "#e8e8f0";
  ctx.fillText(`Respawning in ${Math.ceil(remaining)}s`, canvas.width / 2, canvas.height / 2 + 16);
  ctx.restore();
}

export function renderDungeon(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  dungeon: DungeonSnapshot,
  heroes: readonly HeroSnapshot[],
  minions: readonly MinionSnapshot[],
  groundEffects: readonly GroundEffectSnapshot[],
  flameBarrierRow: number,
  role: Role | null,
  abilityPreview: AbilityPreview | null,
  heroSpellPreview: HeroSpellPreview | null,
  showAttackRange: boolean,
  spellCastHint: SpellCastHint | null,
): void {
  ctx.fillStyle = "#0b0b10";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const camera = computeCamera(canvas, dungeon, heroes, role);

  // Play-area borders — the dungeon has a fixed width; without these the
  // edge is only ever implied by where floor tiles happen to stop.
  const leftEdgeX = camera.xOffset;
  const rightEdgeX = camera.xOffset + dungeon.width * camera.tilePx;
  ctx.save();
  ctx.strokeStyle = "#5a5a6a";
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(leftEdgeX, 0);
  ctx.lineTo(leftEdgeX, canvas.height);
  ctx.moveTo(rightEdgeX, 0);
  ctx.lineTo(rightEdgeX, canvas.height);
  ctx.stroke();
  ctx.restore();

  fillTiles(ctx, camera, dungeon.floorTiles, "#2a2a3a");
  // Rows that already earned their "line clear" gold get a warm tint over the base floor color (design doc: doesn't disappear, unlike tetris).
  if (dungeon.goldLineRows.length > 0) {
    ctx.save();
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = "#e0c040";
    for (const row of dungeon.goldLineRows) {
      const { x, y } = tileToScreen(camera, 0, row);
      ctx.fillRect(x, y, dungeon.width * camera.tilePx, camera.tilePx);
    }
    ctx.restore();
  }
  fillTiles(ctx, camera, dungeon.foundations, "#4a3a2a");

  // Faded target shapes (design doc's "Target shapes") — outlined only, not filled, so they read as "not yet built" rather than an obstacle.
  ctx.save();
  ctx.strokeStyle = "rgba(224, 196, 64, 0.5)";
  ctx.lineWidth = 2;
  ctx.setLineDash([3, 3]);
  for (const shape of dungeon.targetShapes) {
    for (const [col, row] of shape.cells) {
      const { x, y } = tileToScreen(camera, col, row);
      ctx.strokeRect(x + 2, y + 2, camera.tilePx - 4, camera.tilePx - 4);
    }
  }
  ctx.restore();

  // A hard wall behind the front of the dungeon (see
  // CONFIG.architectBehindFrontierRows) — only the architect needs to see
  // it, since it's their movement that's constrained by it.
  if (role === "architect") {
    const wallRow = Math.max(0, dungeon.frontierRow - CONFIG.architectBehindFrontierRows);
    const wallScreenY = pointToScreen(camera, 0, wallRow).y;
    if (wallScreenY > -camera.tilePx && wallScreenY < canvas.height + camera.tilePx) {
      ctx.save();
      ctx.strokeStyle = "rgba(224, 90, 90, 0.6)";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 6]);
      ctx.beginPath();
      ctx.moveTo(0, wallScreenY);
      ctx.lineTo(canvas.width, wallScreenY);
      ctx.stroke();
      ctx.restore();
    }

    // The architect isn't allowed to *place* a piece too far ahead of the
    // heroes either (see CONFIG.architectAheadOfHeroesRows) — a second
    // dashed line, same red-ish treatment as the wall above but marking the
    // front limit instead of the back one. Unlike that wall, a piece can
    // still be freely moved/rotated past this line to scout ahead; it just
    // can't legally come down there (see dungeon.ts's architectPlace).
    const virtualFrontierScreenY = pointToScreen(camera, 0, dungeon.virtualFrontierRow).y;
    if (virtualFrontierScreenY > -camera.tilePx && virtualFrontierScreenY < canvas.height + camera.tilePx) {
      ctx.save();
      ctx.strokeStyle = "rgba(224, 90, 90, 0.6)";
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 6]);
      ctx.beginPath();
      ctx.moveTo(0, virtualFrontierScreenY);
      ctx.lineTo(canvas.width, virtualFrontierScreenY);
      ctx.stroke();
      ctx.restore();
    }
  }

  // Minion markers and the checkpoint are the architect's own planning
  // info — heroes don't see them (or need to; they only interact with what
  // actually gets built, not what's marked to be).
  if (role === "architect") {
    // Minion markers: a small red diamond in the void — the architect is
    // incentivized to cover these (bothers the heroes, buys pressure).
    ctx.fillStyle = "#8a2a2a";
    for (const [col, row] of dungeon.minionMarkers) {
      const { x, y } = tileToScreen(camera, col, row);
      const cx = x + camera.tilePx / 2;
      const cy = y + camera.tilePx / 2;
      const s = camera.tilePx * 0.22;
      ctx.beginPath();
      ctx.moveTo(cx, cy - s);
      ctx.lineTo(cx + s, cy);
      ctx.lineTo(cx, cy + s);
      ctx.lineTo(cx - s, cy);
      ctx.closePath();
      ctx.fill();
    }

    // Checkpoint marker: a ring in the void.
    const [oc, orow] = dungeon.checkpoint;
    const objScreen = tileToScreen(camera, oc, orow);
    ctx.strokeStyle = "#e0c040";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(objScreen.x + camera.tilePx / 2, objScreen.y + camera.tilePx / 2, camera.tilePx * 0.35, 0, Math.PI * 2);
    ctx.stroke();
  }

  drawDoorsKeysChestsAndHazards(ctx, camera, dungeon);

  // Active piece: the architect's own piece, shown solid; other clients see
  // it as a translucent preview so heroes can anticipate where it'll land.
  // An explosive piece (design doc extension) gets a distinct fiery color
  // instead — it destroys foundations it's moved into rather than placing
  // as floor.
  const activeColor = dungeon.activePiece.isExplosive ? "#ff6a2a" : role === "architect" ? "#5ad1a8" : "#3a5ad180";
  fillTiles(ctx, camera, dungeon.activePiece.cells, activeColor);
  if (dungeon.activePiece.specialCell) {
    const { cell, kind } = dungeon.activePiece.specialCell;
    const { x, y } = tileToScreen(camera, cell[0], cell[1]);
    HAZARD_ICON_KIND[kind](ctx, x + camera.tilePx / 2, y + camera.tilePx / 2, camera.tilePx * 0.5);
  }

  drawGroundEffects(ctx, camera, groundEffects);

  if (abilityPreview && role === "architect") {
    const center = pointToScreen(camera, abilityPreview.point.x, abilityPreview.point.y);
    const r = ABILITY_PREVIEW_RADIUS[abilityPreview.ability] * camera.tilePx;
    ctx.save();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.arc(center.x, center.y, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  if (heroSpellPreview) drawHeroSpellPreview(ctx, camera, heroSpellPreview);
  if (spellCastHint) drawSpellCastHint(ctx, spellCastHint);

  // Flame barrier: a glowing line spanning the full width at its current
  // (continuously-advancing) row. Everything at or below this line is
  // taking damage.
  const barrierScreenY = pointToScreen(camera, 0, flameBarrierRow).y;
  if (barrierScreenY > -camera.tilePx && barrierScreenY < canvas.height + camera.tilePx) {
    const gradient = ctx.createLinearGradient(0, barrierScreenY, 0, barrierScreenY + camera.tilePx * 1.5);
    gradient.addColorStop(0, "#ff6a2a");
    gradient.addColorStop(1, "rgba(255, 106, 42, 0)");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, barrierScreenY, canvas.width, camera.tilePx * 1.5);
    ctx.strokeStyle = "#ffcf5c";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(0, barrierScreenY);
    ctx.lineTo(canvas.width, barrierScreenY);
    ctx.stroke();
  }

  // Auto-attack range: a faint dashed circle around each living hero for the
  // first few seconds of the match (see CONFIG.heroAttackRangeShowSeconds) —
  // long enough to learn each class's reach, then it fades out of the way.
  if (showAttackRange) {
    ctx.save();
    ctx.strokeStyle = "rgba(224, 224, 96, 0.5)";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 5]);
    for (const hero of heroes) {
      if (hero.hp <= 0) continue;
      const { x, y } = pointToScreen(camera, hero.x, hero.y);
      ctx.beginPath();
      ctx.arc(x, y, hero.attackRange * camera.tilePx, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
  }

  // Heroes: colored discs, own hero gets a white ring so it's easy to spot.
  // Dead heroes render faded with an X.
  for (const hero of heroes) {
    const { x, y } = pointToScreen(camera, hero.x, hero.y);
    const r = camera.tilePx * 0.32;
    const alive = hero.hp > 0;

    ctx.globalAlpha = alive ? 1 : 0.35;
    ctx.fillStyle = HERO_COLORS[hero.role] ?? "#e8e8f0";
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();

    // A soft ring while *any* status is active (buff or debuff) — which
    // kind(s) are colored in the chip row above the HP bar below. Covers
    // Hunter's self-buff spell 2, Warrior's self-buff spell 2, or anything
    // else that lands a status on a character, uniformly.
    if (hero.statuses.length > 0) {
      ctx.beginPath();
      ctx.arc(x, y, r * 1.5, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(90, 209, 168, 0.7)";
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    ctx.fillStyle = "#0b0b10";
    ctx.font = `${Math.round(r)}px system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(CLASS_LABELS[hero.class], x, y + 1);
    ctx.textAlign = "left";

    if (hero.hasKey) drawKeyIcon(ctx, x + r * 0.9, y - r * 0.9, r * 0.7);

    if (hero.godMode) {
      // Gold ring takes priority over the "this is you" ring — god mode is
      // the more important thing to signal to everyone at the table.
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.strokeStyle = "#e0c040";
      ctx.lineWidth = 3;
      ctx.stroke();
    } else if (hero.role === role) {
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    ctx.globalAlpha = 1;

    if (alive) {
      // Auto-attack cooldown bar right above the head (0 = just attacked,
      // fills up as it becomes ready again), HP bar above that, status
      // chips above that.
      const barW = camera.tilePx * 0.8;
      const atkBarH = 3;
      const hpBarH = 4;
      const barX = x - barW / 2;
      const atkBarY = y - r - atkBarH - 2;
      const hpBarY = atkBarY - hpBarH - 2;

      drawBar(ctx, barX, atkBarY, barW, atkBarH, hero.attackCooldownPct, hero.attackCooldownPct >= 1 ? "#e0e060" : "#6a6a3a");

      const pct = Math.max(0, hero.hp / hero.maxHp);
      ctx.fillStyle = "#3a1a1a";
      ctx.fillRect(barX, hpBarY, barW, hpBarH);
      ctx.fillStyle = pct > 0.3 ? "#5ad17a" : "#e05a5a";
      ctx.fillRect(barX, hpBarY, barW * pct, hpBarH);
      drawStatusChips(ctx, x, hpBarY - 6, hero.statuses);
    } else {
      ctx.strokeStyle = "#e8e8f0";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x - r * 0.6, y - r * 0.6);
      ctx.lineTo(x + r * 0.6, y + r * 0.6);
      ctx.moveTo(x + r * 0.6, y - r * 0.6);
      ctx.lineTo(x - r * 0.6, y + r * 0.6);
      ctx.stroke();

      drawCorpseReviveBar(ctx, x, y, r, hero);
      if (hero.role === role) drawDeathCountdownCenter(ctx, canvas, hero);
    }
  }

  // Minions: small triangles (dead ones are already gone server-side, no
  // death styling needed here). Super minions are drawn bigger with a
  // distinct outline; slowed minions get a blue tint (derived from the same
  // `statuses` list the chip row above reads — see drawStatusChips).
  for (const minion of minions) {
    const { x, y } = pointToScreen(camera, minion.x, minion.y);
    const r = camera.tilePx * (minion.isSuper ? 0.42 : 0.24);
    const slowed = minion.statuses.some((s) => s.kind === "slowed");

    ctx.fillStyle = slowed ? "#5a8ec0" : "#c04a3a";
    ctx.beginPath();
    ctx.moveTo(x, y - r);
    ctx.lineTo(x + r, y + r);
    ctx.lineTo(x - r, y + r);
    ctx.closePath();
    ctx.fill();
    if (minion.isSuper) {
      ctx.strokeStyle = "#ffcf5c";
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    const barW = camera.tilePx * (minion.isSuper ? 0.9 : 0.6);
    const atkBarH = 2.5;
    const hpBarH = 3;
    const barX = x - barW / 2;
    const atkBarY = y - r - atkBarH - 1;
    const hpBarY = atkBarY - hpBarH - 2;

    drawBar(ctx, barX, atkBarY, barW, atkBarH, minion.attackCooldownPct, minion.attackCooldownPct >= 1 ? "#e0e060" : "#6a6a3a");

    const pct = Math.max(0, minion.hp / minion.maxHp);
    ctx.fillStyle = "#3a1a1a";
    ctx.fillRect(barX, hpBarY, barW, hpBarH);
    ctx.fillStyle = "#e0a040";
    ctx.fillRect(barX, hpBarY, barW * pct, hpBarH);
    drawStatusChips(ctx, x, hpBarY - 6, minion.statuses);
  }

  // Low checkpoint-timer warning (see CONFIG.checkpointTimerLowSeconds) — a
  // pulsing red border, architect-only since they're the one who actually
  // loses when it hits 0 (the matching audio cue lives in src/main.ts).
  if (role === "architect" && dungeon.checkpointTimerRemaining > 0 && dungeon.checkpointTimerRemaining <= CONFIG.checkpointTimerLowSeconds) {
    const pulse = 0.35 + 0.35 * Math.abs(Math.sin(Date.now() / 200));
    ctx.save();
    ctx.globalAlpha = pulse;
    ctx.strokeStyle = "#ff2a2a";
    ctx.lineWidth = 14;
    ctx.strokeRect(7, 7, canvas.width - 14, canvas.height - 14);
    ctx.restore();
  }
}

const HUD_PADDING = 12;
const HUD_LINE_HEIGHT = 22;
const HUD_LABEL_WIDTH = 92; // px reserved for the text label before a bar starts
const HUD_BAR_WIDTH = 130;
const HUD_BAR_HEIGHT = 10;
const HUD_PANEL_WIDTH = HUD_PADDING * 2 + HUD_LABEL_WIDTH + HUD_BAR_WIDTH;
const HUD_PIECE_PREVIEW_SIZE = 40;

function drawBar(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, pct: number, fg: string): void {
  ctx.fillStyle = "#1c1c26";
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = fg;
  ctx.fillRect(x, y, w * Math.max(0, Math.min(1, pct)), h);
  ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, w, h);
}

/** A small tetromino preview so the architect can see the next piece's shape, not just its letter — or a bomb icon if it'll be an explosive instead (design doc extension). */
function drawPiecePreview(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, type: PieceType, isExplosive: boolean): void {
  if (isExplosive) {
    const cx = x + size / 2;
    const cy = y + size / 2;
    ctx.fillStyle = "#ff6a2a";
    ctx.beginPath();
    ctx.arc(cx, cy, size * 0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#ffcf5c";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy - size * 0.3);
    ctx.lineTo(cx + size * 0.18, cy - size * 0.48);
    ctx.stroke();
    return;
  }
  const offsets = pieceOffsets(type);
  const cell = size / 4; // the widest piece (I) is 4 tiles, so a 4-cell box always fits everything
  const w = pieceWidth(offsets) * cell;
  const h = pieceHeight(offsets) * cell;
  const xOff = x + (size - w) / 2;
  const yOff = y + (size - h) / 2;
  ctx.fillStyle = "#5ad1a8";
  for (const o of offsets) {
    ctx.fillRect(xOff + o.dx * cell + 1, yOff + o.dy * cell + 1, cell - 2, cell - 2);
  }
}

const SPELL_LABELS = ["Q", "E", "R"];

const ABILITY_LABELS: Record<ArchitectAbility, string> = {
  meteor: "Meteor",
  spawnMinions: "Spawn minions",
  spawnSuperMinion: "Spawn super-minion",
};
const ABILITY_KEYS: Record<ArchitectAbility, string> = {
  meteor: "1",
  spawnMinions: "2",
  spawnSuperMinion: "3",
};

const UPGRADE_LABELS: Record<ArchitectUpgrade, string> = {
  minionSpeed: "Minion speed",
  minionHealth: "Minion health",
  minionDamage: "Minion damage",
  minionCount: "Minion count",
};
const UPGRADE_KEYS: Record<ArchitectUpgrade, string> = {
  minionSpeed: "4",
  minionHealth: "5",
  minionDamage: "6",
  minionCount: "7",
};
const UPGRADE_ORDER: readonly ArchitectUpgrade[] = ["minionSpeed", "minionHealth", "minionDamage", "minionCount"];

function upgradeCost(level: number): number {
  return Math.round(CONFIG.architectUpgradeBaseCost * Math.pow(CONFIG.architectUpgradeCostGrowth, level));
}

export function renderHud(
  ctx: CanvasRenderingContext2D,
  dungeon: DungeonSnapshot,
  heroes: readonly HeroSnapshot[],
  architectGodMode: boolean,
  architectGold: number,
  minionUpgradeLevels: MinionUpgradeLevels,
  role: Role | null,
  armedAbility: ArchitectAbility | null,
): void {
  const myHero = role ? heroes.find((h) => h.role === role) : undefined;

  // Timer bar is scaled against the *initial* duration T purely as a visual
  // reference point — reaching checkpoints adds T repeatedly, so the timer
  // can (and should) grow past that over a good match. A full bar just
  // means "comfortably ahead", not "capped".
  const timerPct = Math.min(1, dungeon.checkpointTimerRemaining / CONFIG.checkpointTimerBonusSeconds);
  const timerColor = timerPct < 0.25 ? "#e05a5a" : timerPct < 0.5 ? "#e0a040" : "#e0c040";

  let panelHeight = HUD_PADDING * 2 + HUD_LINE_HEIGHT * 2;
  if (role === "architect") {
    panelHeight += HUD_PIECE_PREVIEW_SIZE + HUD_LINE_HEIGHT; // gold line
    panelHeight += (architectGodMode ? HUD_LINE_HEIGHT : 0);
    panelHeight += HUD_LINE_HEIGHT * 3; // ability hotkey rows
    panelHeight += HUD_LINE_HEIGHT * UPGRADE_ORDER.length; // upgrade hotkey rows
  }
  if (myHero) {
    panelHeight += HUD_LINE_HEIGHT * 2; // hp, xp
    panelHeight += HUD_LINE_HEIGHT * 4; // "Spells:" header + one line per spell
    panelHeight += (myHero.godMode ? HUD_LINE_HEIGHT : 0) + (myHero.invulnerableRemaining > 0 ? HUD_LINE_HEIGHT : 0) + (myHero.hp <= 0 ? HUD_LINE_HEIGHT : 0);
    if (myHero.statuses.length > 0) panelHeight += HUD_LINE_HEIGHT;
    if (myHero.hasKey) panelHeight += HUD_LINE_HEIGHT;
  }

  ctx.fillStyle = "rgba(15, 15, 22, 0.78)";
  ctx.fillRect(0, 0, HUD_PANEL_WIDTH, panelHeight);

  const textX = HUD_PADDING;
  const barX = HUD_PADDING + HUD_LABEL_WIDTH;
  let y = HUD_PADDING;
  ctx.font = "13px system-ui, sans-serif";
  ctx.textBaseline = "middle";

  ctx.fillStyle = "#e8e8f0";
  ctx.fillText(`Role: ${role ?? "?"}`, textX, y + HUD_LINE_HEIGHT / 2);
  y += HUD_LINE_HEIGHT;

  ctx.fillStyle = "#e8e8f0";
  ctx.fillText(`Timer: ${dungeon.checkpointTimerRemaining.toFixed(1)}s`, textX, y + HUD_LINE_HEIGHT / 2);
  drawBar(ctx, barX, y + (HUD_LINE_HEIGHT - HUD_BAR_HEIGHT) / 2, HUD_BAR_WIDTH, HUD_BAR_HEIGHT, timerPct, timerColor);
  y += HUD_LINE_HEIGHT;

  if (role === "architect") {
    ctx.fillStyle = "#e8e8f0";
    ctx.fillText(dungeon.nextPieceIsExplosive ? "Next piece: EXPLOSIVE" : "Next piece:", textX, y + HUD_PIECE_PREVIEW_SIZE / 2);
    drawPiecePreview(ctx, barX, y, HUD_PIECE_PREVIEW_SIZE, dungeon.nextPieceType, dungeon.nextPieceIsExplosive);
    y += HUD_PIECE_PREVIEW_SIZE;

    ctx.fillStyle = "#e0c040";
    ctx.fillText(`Gold: ${architectGold}`, textX, y + HUD_LINE_HEIGHT / 2);
    y += HUD_LINE_HEIGHT;

    if (architectGodMode) {
      ctx.fillStyle = "#e0c040";
      ctx.fillText("GOD MODE — timer frozen", textX, y + HUD_LINE_HEIGHT / 2);
      y += HUD_LINE_HEIGHT;
    }

    for (const ability of ["meteor", "spawnMinions", "spawnSuperMinion"] as const) {
      const cost = CONFIG.architectAbilityCosts[ability];
      const affordable = architectGold >= cost;
      ctx.fillStyle = ability === armedAbility ? "#5ad1a8" : affordable ? "#e8e8f0" : "#6a6a7a";
      const armedTag = ability === armedAbility ? " (armed, click to cast)" : "";
      // Both minion-spawning abilities are paid, active spawns — unlike a
      // map minion marker, they never grant xp (see
      // CONFIG.xpPerMinionKill's comment), which is easy to assume
      // otherwise since they still cost gold like everything else here.
      const noXpTag = ability === "spawnMinions" || ability === "spawnSuperMinion" ? " (doesn't grant xp to heroes)" : "";
      ctx.fillText(`[${ABILITY_KEYS[ability]}] ${ABILITY_LABELS[ability]} — ${cost}g${armedTag}${noXpTag}`, textX, y + HUD_LINE_HEIGHT / 2);
      y += HUD_LINE_HEIGHT;
    }

    for (const upgrade of UPGRADE_ORDER) {
      const level = minionUpgradeLevels[upgrade];
      const cost = upgradeCost(level);
      const affordable = architectGold >= cost;
      ctx.fillStyle = affordable ? "#e8e8f0" : "#6a6a7a";
      ctx.fillText(`[${UPGRADE_KEYS[upgrade]}] ${UPGRADE_LABELS[upgrade]} Lv${level} — ${cost}g`, textX, y + HUD_LINE_HEIGHT / 2);
      y += HUD_LINE_HEIGHT;
    }
  }

  if (myHero) {
    const hpPct = myHero.maxHp > 0 ? myHero.hp / myHero.maxHp : 0;
    ctx.fillStyle = "#e8e8f0";
    ctx.fillText(`HP: ${Math.ceil(myHero.hp)}/${myHero.maxHp}`, textX, y + HUD_LINE_HEIGHT / 2);
    drawBar(ctx, barX, y + (HUD_LINE_HEIGHT - HUD_BAR_HEIGHT) / 2, HUD_BAR_WIDTH, HUD_BAR_HEIGHT, hpPct, hpPct > 0.3 ? "#5ad17a" : "#e05a5a");
    y += HUD_LINE_HEIGHT;

    const xpPct = myHero.xpToNextLevel > 0 ? myHero.xp / myHero.xpToNextLevel : 0;
    ctx.fillStyle = "#e8e8f0";
    ctx.fillText(`Lv ${myHero.level} ${myHero.class}: ${Math.floor(myHero.xp)}/${myHero.xpToNextLevel}`, textX, y + HUD_LINE_HEIGHT / 2);
    drawBar(ctx, barX, y + (HUD_LINE_HEIGHT - HUD_BAR_HEIGHT) / 2, HUD_BAR_WIDTH, HUD_BAR_HEIGHT, xpPct, "#5ad1a8");
    y += HUD_LINE_HEIGHT;

    ctx.fillStyle = "#9a9ab0";
    ctx.fillText("Spells:", textX, y + HUD_LINE_HEIGHT / 2);
    y += HUD_LINE_HEIGHT;
    for (let i = 0; i < 3; i++) {
      const cooldown = myHero.spellCooldowns[i];
      const spellCfg = (CONFIG.heroSpells[myHero.class] as Record<string, { cooldown: number }>)[`spell${i + 1}`];
      const ready = cooldown <= 0;
      const pct = ready ? 1 : 1 - cooldown / spellCfg.cooldown;
      ctx.fillStyle = ready ? "#e8e8f0" : "#9a9ab0";
      ctx.fillText(`[${SPELL_LABELS[i]}]`, textX, y + HUD_LINE_HEIGHT / 2);
      drawBar(ctx, barX, y + (HUD_LINE_HEIGHT - HUD_BAR_HEIGHT) / 2, HUD_BAR_WIDTH, HUD_BAR_HEIGHT, pct, ready ? "#5ad1a8" : "#3a5ad1");
      ctx.fillStyle = "#9a9ab0";
      ctx.font = "11px system-ui, sans-serif";
      ctx.fillText(HERO_SPELL_DESCRIPTIONS[myHero.class][(i + 1) as SpellSlot], barX + HUD_BAR_WIDTH + 8, y + HUD_LINE_HEIGHT / 2);
      ctx.font = "13px system-ui, sans-serif";
      y += HUD_LINE_HEIGHT;
    }

    if (myHero.statuses.length > 0) {
      ctx.fillStyle = "#e0c040";
      ctx.fillText(myHero.statuses.map((s) => s.kind).join(", "), textX, y + HUD_LINE_HEIGHT / 2);
      y += HUD_LINE_HEIGHT;
    }

    if (myHero.hasKey) {
      ctx.fillStyle = "#e0c040";
      drawKeyIcon(ctx, textX + 6, y + HUD_LINE_HEIGHT / 2, 12);
      ctx.fillText("Holding a key", textX + 18, y + HUD_LINE_HEIGHT / 2);
      y += HUD_LINE_HEIGHT;
    }

    if (myHero.godMode) {
      ctx.fillStyle = "#e0c040";
      ctx.fillText("GOD MODE — immune to damage", textX, y + HUD_LINE_HEIGHT / 2);
      y += HUD_LINE_HEIGHT;
    }
    if (myHero.invulnerableRemaining > 0) {
      ctx.fillStyle = "#5ad1a8";
      ctx.fillText(`Invulnerable: ${myHero.invulnerableRemaining.toFixed(1)}s`, textX, y + HUD_LINE_HEIGHT / 2);
      y += HUD_LINE_HEIGHT;
    }
    if (myHero.hp <= 0) {
      ctx.fillStyle = "#e05a5a";
      ctx.fillText("You have fallen.", textX, y + HUD_LINE_HEIGHT / 2);
    }
  }

  ctx.textBaseline = "alphabetic"; // restore the canvas default so other draw calls aren't affected
}
